import { ChangeDetectionStrategy, Component } from '@angular/core';
import { SectionHeaderComponent } from '@app/widgets';
import { TuiHint, TuiIcon } from '@taiga-ui/core';
import { TuiTooltip } from '@taiga-ui/kit';

interface ComparisonFeature {
  category: string;
  features: {
    name: string;
    capawesome: boolean | string;
    capgo: boolean | string;
    capawesomeAdvantage?: boolean;
    tooltip?: string;
  }[];
}

@Component({
  selector: 'app-compare-capgo-table-section',
  imports: [SectionHeaderComponent, TuiIcon, TuiHint, TuiTooltip],
  templateUrl: './compare-capgo-table-section.component.html',
  styleUrl: './compare-capgo-table-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompareCapgoTableSectionComponent {
  protected readonly comparisonData: ComparisonFeature[] = [
    {
      category: 'Live Updates',
      features: [
        {
          name: 'Code Signing',
          capawesome: true,
          capgo: true,
        },
        {
          name: 'Delta Updates',
          capawesome: true,
          capgo: true,
          tooltip:
            'Only the changed files of your app are downloaded, reducing update size and improving performance.',
        },
        {
          name: 'Rollouts',
          capawesome: true,
          capgo: false,
          capawesomeAdvantage: true,
          tooltip:
            'Gradually release updates to a percentage of your users to monitor performance and catch issues before a full rollout.',
        },
        {
          name: 'Version Distribution Strategy',
          capawesome: 'Always latest (Ionic Appflow approach)',
          capgo: 'Semver (Custom approach)',
          capawesomeAdvantage: true,
          tooltip:
            'No version specification required—simply register updates and users automatically receive the latest version, just like Ionic Appflow.',
        },
        {
          name: 'SDK License',
          capawesome: 'MIT',
          capgo: 'MPL 2.0',
          capawesomeAdvantage: true,
          tooltip:
            'The MIT license allows you to modify and patch the Live Updates SDK for your specific needs without being required to open source your changes, giving you complete flexibility.',
        },
        {
          name: 'SDK Size',
          capawesome: '294 KB (v7.2.2)',
          capgo: '986 KB (v7.33.0)',
          capawesomeAdvantage: true,
          tooltip:
            'The unpacked size of the Live Updates SDK in your app. A smaller SDK size means faster app startup times and reduced storage footprint.',
        },
        {
          name: 'SDK Dependencies (Android)',
          capawesome: '2 (v7.2.2)',
          capgo: '6 (v7.33.0)',
          capawesomeAdvantage: true,
          tooltip:
            'Number of additional dependencies required by the Live Updates SDK on Android. Fewer dependencies mean less complexity, faster builds, and reduced potential for conflicts.',
        },
        {
          name: 'SDK Dependencies (iOS)',
          capawesome: '2 (v7.2.2)',
          capgo: '4 (v7.33.0)',
          capawesomeAdvantage: true,
          tooltip:
            'Number of additional dependencies required by the Live Updates SDK on iOS. Fewer dependencies mean less complexity, faster builds, and reduced potential for conflicts.',
        },
      ],
    },
    {
      category: 'Native Builds',
      features: [
        {
          name: 'Android Build Infrastructure',
          capawesome: 'M4 macOS runners',
          capgo: 'Docker containers',
          capawesomeAdvantage: true,
          tooltip:
            'Capawesome Cloud uses M4 macOS runners for both iOS and Android builds to ensure consistency and optimal performance. Capgo uses Cloudflare containers for Android builds.',
        },
        {
          name: 'iOS Build Infrastructure',
          capawesome: 'M4 macOS runners',
          capgo: 'M4 macOS runners (no hardware specs shared)',
          capawesomeAdvantage: true,
          tooltip:
            'Capawesome Cloud offers M4 macOS instances with up to 14 CPU cores, 64 GB RAM, and 250 GB storage for iOS builds. Capgo uses M4 macOS runners but does not publicly share hardware specifications.',
        },
        {
          name: 'Build Stack Options',
          capawesome: 'Multiple stacks (macOS Sequoia and Tahoe)',
          capgo: 'Single stack per platform',
          capawesomeAdvantage: true,
          tooltip:
            'Capawesome Cloud offers multiple build stacks for maximum flexibility and compatibility with different Xcode versions and dependencies.',
        },
        {
          name: 'Git Integration',
          capawesome: 'GitHub, GitLab, Bitbucket, Azure DevOps',
          capgo: 'None',
          capawesomeAdvantage: true,
          tooltip:
            'Capawesome Cloud supports full Git integration with automatic builds across all major platforms.',
        },
        {
          name: 'Trigger Options',
          capawesome: 'Console + CLI + API',
          capgo: 'CLI only',
          capawesomeAdvantage: true,
          tooltip:
            'Capawesome Cloud builds can be triggered from the web console, command line, or API for maximum flexibility.',
        },
        {
          name: 'Certificate Management',
          capawesome: 'Easy management via Console',
          capgo: 'None',
          capawesomeAdvantage: true,
          tooltip:
            'Capawesome Cloud provides easy signing certificate management through the web console, CLI, or API.',
        },
        {
          name: 'Environment Variables and Secrets',
          capawesome: true,
          capgo: false,
          capawesomeAdvantage: true,
          tooltip:
            'Securely store and inject environment variables and secrets into your builds, perfect for API keys, credentials, and build-time configuration.',
        },
        {
          name: 'Centralized Build Tracking',
          capawesome: true,
          capgo: 'No build details',
          capawesomeAdvantage: true,
          tooltip:
            'Monitor all your native builds in one place with full visibility into build history, logs, and statuses.',
        },
      ],
    },
    {
      category: 'App Store Publishing',
      features: [
        {
          name: 'App Store Submission',
          capawesome: true,
          capgo: true,
        },
        {
          name: 'TestFlight Submission',
          capawesome: true,
          capgo: true,
        },
        {
          name: 'Google Play Submission',
          capawesome: true,
          capgo: true,
        },
        {
          name: 'Submit from Anywhere',
          capawesome: 'Web Console',
          capgo: 'CLI only',
          capawesomeAdvantage: true,
          tooltip:
            'Submit updates from any device using the Web Console without needing CLI access.',
        },
        {
          name: 'Centralized Deployment Tracking',
          capawesome: true,
          capgo: 'No deployment details',
          capawesomeAdvantage: true,
          tooltip:
            'Monitor all your app store deployments in one place with full visibility into submission history, logs, and statuses.',
        },
      ],
    },
    {
      category: 'Team & Support',
      features: [
        {
          name: 'Official Ionic Developer Experts',
          capawesome: true,
          capgo: false,
          capawesomeAdvantage: true,
          tooltip:
            'The Capawesome team is part of the official Ionic Developer Experts Program.',
        },
        {
          name: 'Official Capacitor Community Members',
          capawesome: true,
          capgo: false,
          capawesomeAdvantage: true,
          tooltip:
            'The Capawesome team is part of the official Capacitor Community GitHub organization.',
        },
      ],
    },
    {
      category: 'Pricing',
      features: [
        {
          name: 'Starting price',
          capawesome: '$9/month',
          capgo: '$14/month',
          capawesomeAdvantage: true,
        },
        {
          name: 'Build Multiplier',
          capawesome: 'No',
          capgo: 'Yes',
          capawesomeAdvantage: true,
          tooltip:
            'Capawesome Cloud charges the same rate for iOS and Android builds for simple, predictable pricing.',
        },
        {
          name: 'Free tier',
          capawesome: true,
          capgo: false,
          capawesomeAdvantage: true,
          tooltip:
            'Deliver Live Updates for free without any trial or credit card required.',
        },
      ],
    },
    {
      category: 'Enterprise & Security',
      features: [
        {
          name: 'SOC 2 Type II',
          capawesome: true,
          capgo: true,
          tooltip:
            'SOC 2 Type II certified for stringent data security and privacy standards.',
        },
        {
          name: 'ISO 27001',
          capawesome: true,
          capgo: false,
          capawesomeAdvantage: true,
          tooltip:
            'ISO 27001 certified for international information security standards.',
        },
        {
          name: 'SSO',
          capawesome: true,
          capgo: false,
          capawesomeAdvantage: true,
          tooltip: 'Single Sign-On support for enterprise authentication.',
        },
        {
          name: 'Audit Logs',
          capawesome: '15 tables',
          capgo: '5 tables',
          capawesomeAdvantage: true,
          tooltip:
            'Comprehensive audit logging for compliance and security monitoring. Capawesome Cloud provides extensive coverage across 15 tables for complete visibility into all organizational activities.',
        },
        {
          name: 'Teams',
          capawesome: true,
          capgo: false,
          capawesomeAdvantage: true,
          tooltip:
            'Create and manage multiple teams within your organization for more granular control over user permissions and resource access.',
        },
        // {
        //   name: 'Last Security Incident',
        //   See https://github.com/Cap-go/capgo/commit/d74a0ed56e6ac40976e72a372c419613bf58d80a
        // },
        // {
        //   name: 'Last Uptime Incident',
        //   See https://status.capgo.app/history/inci_OyqJje20EPNzxI (January 13, 2026)
        // },
      ],
    },
    {
      category: 'Quality & Stability',
      features: [
        {
          name: 'Patch releases (last 90 days)*',
          capawesome: '1',
          capgo: '48',
          capawesomeAdvantage: true,
          tooltip:
            'Fewer patch releases generally indicate a more stable product. Data sourced from GitHub issues.',
        },
        {
          name: 'Bug reports (last 90 days)*',
          capawesome: '3',
          capgo: '>20',
          capawesomeAdvantage: true,
          tooltip:
            'Fewer bug reports generally indicate a more stable product. Data sourced from GitHub issues.',
        },
      ],
    },
  ];
}
